import { defineStore } from 'pinia'
import apiClient from '@/apiClient.js'
import { getPresenceTabId } from '@/utils/presenceSession'

let initializationPromise = null

export let useAuthStore = defineStore('auth', {
  state() {
    return {
      authenticated: false,
      initialized: false,
      loginError: false,
      agent: null,
      roles: JSON.parse(localStorage.getItem('roles')) || [], // Load roles from localStorage
      user: JSON.parse(localStorage.getItem('user')) || null,
    }
  },

  actions: {
    setLoggedIn() {
      this.authenticated = true
    },

    clearSession() {
      localStorage.removeItem('authenticated')
      localStorage.removeItem('user')
      localStorage.removeItem('roles')
      this.authenticated = false
      this.initialized = true
      this.roles = []
      this.user = null
      this.agent = null
    },

    setSession(session) {
      this.roles = Array.isArray(session?.roles) ? session.roles : []
      this.user = session?.user || null
      this.agent = session?.agent || null
      this.authenticated = Boolean(this.user)
      this.initialized = true

      if (this.authenticated) {
        localStorage.setItem('roles', JSON.stringify(this.roles))
        localStorage.setItem('user', JSON.stringify(this.user))
        this.renewTimeoutCookie()
      } else {
        this.clearSession()
      }
    },

    async initialize() {
      if (this.initialized) {
        return this.authenticated
      }

      if (initializationPromise) {
        return initializationPromise
      }

      initializationPromise = (async () => {
        try {
          const response = await apiClient.get('/api/user/session', {
            suppressErrorAlert: true,
          })
          this.setSession(response.data)
          return true
        } catch {
          this.clearSession()
          return false
        } finally {
          this.initialized = true
          initializationPromise = null
        }
      })()

      return initializationPromise
    },

    async tryAgentLogin(secret) {
      try {
        await apiClient.get('/sanctum/csrf-cookie')
        await apiClient.post('/api/agent/session', {}, {
          headers: { Authorization: `Bearer ${secret}` },
          suppressErrorAlert: true,
        })
        const response = await apiClient.get('/api/user/session', { suppressErrorAlert: true })
        this.setSession(response.data)
        return true
      } catch {
        this.clearSession()
        return false
      }
    },

    async tryLogin(email, password) {
      if (!email.length || !password.length) {
        return false
      }

      try {
        // Ensure Sanctum issues a fresh XSRF token before attempting login
        await apiClient.get('/sanctum/csrf-cookie')

        await apiClient.post('/login', { email, password })

        // Confirm that the browser accepted the regenerated session cookie
        // before protected views start their first batch of requests.
        const sessionResponse = await apiClient.get('/api/user/session', {
          suppressErrorAlert: true,
        })
        this.setSession(sessionResponse.data)
        return true
      } catch (e) {
        return false
      }
    },

    renewTimeoutCookie() {
      let now = new Date()
      let expiry = now.getTime() + 2 * 60 * 60 * 1000 // 2 hour expiry
      localStorage.setItem('authenticated', expiry)
    },

    async logout() {
      try {
        if (this.authenticated) {
          await apiClient.post('/api/user/logout', {
            tab_id: getPresenceTabId(),
          })
        }
      } catch (error) {
        if (error.response?.status !== 401 && error.response?.status !== 419) {
          console.error("Logout error:", error)
        }
      } finally {
        this.clearSession()
      }
    },

    clearErrors() {
      this.loginError = false
    },

    setUser(user) {
      this.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },
  },

  getters: {
    isLoggedIn() {
      return this.authenticated
    },

    hasError() {
      return this.loginError
    },

    name() {
      return this.user?.name || ''
    },

    email() {
      return this.user?.email || ''
    },

    hasRole: (state) => (roleName) => {
      const wanted = String(roleName || '').toLowerCase()
      return (state.roles || []).some((role) => String(role || '').toLowerCase() === wanted)
    },

    hasAnyRole: (state) => (roleNames = []) => {
      const roleSet = new Set((state.roles || []).map((role) => String(role || '').toLowerCase()))
      return (roleNames || []).some((roleName) => roleSet.has(String(roleName || '').toLowerCase()))
    },

    hasFinanceRole: (state) => () => {
      const roleSet = new Set((state.roles || []).map((role) => String(role || '').toLowerCase()))
      return roleSet.has('super')
        || roleSet.has('finance-department')
        || roleSet.has('finance-audit')
        || roleSet.has('finance-audits')
    },
  },
})
